import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck, FileText, FormInput, Upload } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTaxYear } from '@/contexts/TaxYearContext';
import { getDocumentStatistics, getUserDocuments, formatFileSize } from '@/services/documentApi';
import { listMyDraftCopies } from '@/services/taxFilingsApi';
import { listForms } from '@/services/formsApi';
import type { FormListItem } from '@/services/formsApi';
import { Document } from '@/types/document';
import {
  DRAFT_COPY_STATUS,
  DRAFT_COPY_STATUS_LABELS,
  TaxFilingDocument,
} from '@/types/taxFilings';
import { FORM_FILING_TYPE } from '@/constants/formConstants';
import { StatCard } from '@/components/common/StatCard';
import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardStats {
  total: number;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedYear } = useTaxYear();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [draftCopies, setDraftCopies] = useState<TaxFilingDocument[]>([]);
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadDashboardData() {
      if (!user) return;

      try {
        const [statsData, documents, draftsData, formsData] = await Promise.all([
          getDocumentStatistics(user.id, selectedYear),
          getUserDocuments(user.id, selectedYear),
          listMyDraftCopies(selectedYear),
          listForms({ taxYear: selectedYear }),
        ]);

        setStats(statsData);
        setRecentDocuments(documents.slice(0, 5));
        setDraftCopies(draftsData);
        setForms(formsData);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadDashboardData();
  }, [user, selectedYear]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-muted-foreground">
          Here's an overview of your tax documents and filing status.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Documents"
          value={stats?.total || 0}
          description={`Documents for ${selectedYear}`}
          icon={FileText}
        />
        <StatCard
          title="Tax Filings"
          value={draftCopies.length}
          description={`Draft copies for ${selectedYear}`}
          icon={FileCheck}
        />
        <StatCard
          title="Tax Forms"
          value={forms.length}
          description={`Individual & business for ${selectedYear}`}
          icon={FormInput}
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Upload documents, work on your tax form, or review tax filings
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={() => navigate('/upload')}>
            <Upload className="mr-2 h-4 w-4" />
            Upload Documents
          </Button>
          <Button variant="outline" onClick={() => navigate('/tax-form')}>
            <FormInput className="mr-2 h-4 w-4" />
            Tax Form
          </Button>
          <Button variant="outline" onClick={() => navigate('/tax-filings')}>
            <FileCheck className="mr-2 h-4 w-4" />
            Tax Filings
          </Button>
          <Button variant="outline" onClick={() => navigate('/documents')}>
            <FileText className="mr-2 h-4 w-4" />
            View All Documents
          </Button>
        </CardContent>
      </Card>

      {/* Tax Form & Filings - two columns on large screens */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tax Form summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FormInput className="h-5 w-5" />
                Tax Form
              </CardTitle>
              <CardDescription>
                Individual and business forms for {selectedYear}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/tax-form')}>
              View all
            </Button>
          </CardHeader>
          <CardContent>
            {forms.length === 0 ? (
              <EmptyState
                icon={FormInput}
                title="No tax forms yet"
                description="Start an Individual or Business form for this year."
                action={{
                  label: 'Go to Tax Form',
                  onClick: () => navigate('/tax-form'),
                }}
              />
            ) : (
              <div className="space-y-3">
                {/* One individual form per year */}
                {(() => {
                  const individualForm = forms.find((f) => f.filingType === FORM_FILING_TYPE.INDIVIDUAL);
                  return (
                    <div
                      key="individual"
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <span className="font-medium">Individual</span>
                      {individualForm ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/tax-form/${individualForm.id}`)}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => navigate('/tax-form')}>
                          Start
                        </Button>
                      )}
                    </div>
                  );
                })()}
                {/* All business forms (any number per year) */}
                {(() => {
                  const businessForms = forms.filter((f) => f.filingType === FORM_FILING_TYPE.BUSINESS);
                  return businessForms.map((form, index) => (
                    <div
                      key={form.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <span className="font-medium">
                        Business{businessForms.length > 1 ? ` ${index + 1}` : ''}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/tax-form/${form.id}`)}
                      >
                        Continue
                      </Button>
                    </div>
                  ));
                })()}
                {/* Add another business form */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-dashed bg-card hover:bg-accent/50 transition-colors">
                  <span className="font-medium text-muted-foreground">
                    {forms.some((f) => f.filingType === FORM_FILING_TYPE.BUSINESS) ? 'Add another business form' : 'Business form'}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => navigate('/tax-form')}>
                    {forms.some((f) => f.filingType === FORM_FILING_TYPE.BUSINESS) ? 'Add' : 'Start'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Tax Filings (draft copies) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Tax Filings
              </CardTitle>
              <CardDescription>
                Draft copies from your admin for {selectedYear}
              </CardDescription>
            </div>
            {draftCopies.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/tax-filings')}>
                View all
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {draftCopies.length === 0 ? (
              <EmptyState
                icon={FileCheck}
                title="No tax filings yet"
                description="Your admin will upload draft copies for you to review."
                action={{
                  label: 'Go to Tax Filings',
                  onClick: () => navigate('/tax-filings'),
                }}
              />
            ) : (
              <div className="space-y-3">
                {draftCopies.slice(0, 5).map((draft) => (
                  <div
                    key={draft.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate text-sm">{draft.name}</p>
                      <Badge
                        variant={
                          draft.status === DRAFT_COPY_STATUS.CLIENT_VERIFIED
                            ? 'success'
                            : draft.status === DRAFT_COPY_STATUS.CLIENT_REJECTED
                              ? 'destructive'
                              : 'secondary'
                        }
                        className="mt-1 text-xs"
                      >
                        {draft.status
                          ? DRAFT_COPY_STATUS_LABELS[draft.status]
                          : 'Unknown'}
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/tax-filings/draft-copies')}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Documents</CardTitle>
            <CardDescription>
              Your most recently uploaded documents
            </CardDescription>
          </div>
          {recentDocuments.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/documents')}>
              View all
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {recentDocuments.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No documents yet"
              description="Start by uploading your tax documents to get started with your filing."
              action={{
                label: 'Upload Documents',
                onClick: () => navigate('/upload'),
              }}
            />
          ) : (
            <div className="space-y-4">
              {recentDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{doc.originalName}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatFileSize(doc.size)}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="hidden sm:inline">{formatDate(doc.uploadedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
